import React, { useState } from "react";
import { useTenants } from "../api/sitroom";
import { OnboardTenantModal } from "../components/OnboardTenantModal";

const slaToneStyle = (tone) => {
  switch (tone) {
    case "red":
      return { color: "#ff7e7c", bg: "rgba(226,75,74,0.12)", border: "rgba(226,75,74,0.45)" };
    case "amber":
      return { color: "#f3b14a", bg: "rgba(243,156,18,0.12)", border: "rgba(243,156,18,0.45)" };
    case "green":
      return { color: "#4ed084", bg: "rgba(39,174,96,0.12)", border: "rgba(39,174,96,0.45)" };
    case "blue":
    default:
      return { color: "#9ec3ec", bg: "rgba(24,95,165,0.14)", border: "rgba(24,95,165,0.5)" };
  }
};

const Avatar = ({ initials }) => (
  <div
    className="rounded-md flex items-center justify-center flex-shrink-0"
    style={{
      width: 36,
      height: 36,
      background: "#0d2f52",
      border: "1px solid rgba(24,95,165,0.5)",
      color: "#9ec3ec",
      fontFamily: "DM Mono, monospace",
      fontSize: 11,
      fontWeight: 500,
    }}
  >
    {initials}
  </div>
);

const IntegrationTag = ({ text }) => {
  const safeText = typeof text === "string" ? text : "";
  const pending = safeText.includes("pending");
  return (
    <span
      style={{
        background: pending ? "rgba(243,156,18,0.10)" : "rgba(24,95,165,0.14)",
        border: `1px solid ${pending ? "rgba(243,156,18,0.4)" : "rgba(24,95,165,0.4)"}`,
        color: pending ? "#f3b14a" : "#9ec3ec",
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        padding: "2px 7px",
        borderRadius: 999,
        letterSpacing: "0.3px",
        display: "inline-block",
        whiteSpace: "nowrap",
      }}
    >
      {safeText}
    </span>
  );
};

export default function Tenants() {
  const { data: tenants = [] } = useTenants();
  const [onboardOpen, setOnboardOpen] = useState(false);
  return (
    <div data-testid="tenants-view">
      {onboardOpen && (
        <OnboardTenantModal
          onClose={() => setOnboardOpen(false)}
          onCreated={() => setOnboardOpen(false)}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            Tenants
          </h1>
          <div className="mt-1.5" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }} data-testid="tenants-caption">
            {tenants.length} active · {(() => {
              const layers = new Set();
              tenants.forEach((t) => {
                if (t.layer) { layers.add(t.layer); return; }
                const m = typeof t.type === "string" && t.type.match(/Layer\s+(\d+)/i);
                if (m) layers.add(parseInt(m[1], 10));
              });
              return layers.size;
            })()} supply chain layers
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOnboardOpen(true)}
          className="px-3 py-2 rounded-md self-start sm:self-auto"
          style={{
            background: "#185FA5",
            color: "#fff",
            fontFamily: "Syne, sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "0.4px",
            cursor: "pointer",
            border: "none",
          }}
          data-testid="onboard-tenant-btn"
        >
          ＋ Onboard Tenant
        </button>
      </div>

      {/* Header (desktop) */}
      <div
        className="hidden sm:grid gap-3 px-4 py-2"
        style={{
          gridTemplateColumns: "38px 1fr 140px 100px 160px",
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        <div />
        <div>Company</div>
        <div>Type</div>
        <div>SLA</div>
        <div>Integrations</div>
      </div>

      <div>
        {tenants.map((t) => {
          const tone = slaToneStyle(t.slaTone);
          const integrations = typeof t.integrations === "string" && t.integrations
            ? t.integrations.split(" + ").filter(Boolean)
            : [];
          return (
            <div
              key={t.id}
              className="row-hover sm:grid sm:gap-3 flex sm:flex-none items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer"
              style={{
                gridTemplateColumns: "38px 1fr 140px 100px 160px",
                background: "#0d0f18",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 6,
                marginBottom: 6,
              }}
              data-testid={`tenant-row-${t.id}`}
            >
              <Avatar initials={t.initials || (t.name || "?").slice(0, 2).toUpperCase()} />
              <div className="min-w-0 flex-1">
                <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8" }}>
                  {t.name || "—"}
                </div>
                <div
                  className="truncate"
                  style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", marginTop: 1 }}
                >
                  {t.id}
                </div>
              </div>
              <div
                className="hidden sm:block"
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10,
                  color: "#9aa1b3",
                  letterSpacing: "0.4px",
                }}
              >
                {t.type || t.tenant_type_key || "—"}
              </div>
              <span
                style={{
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  color: tone.color,
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10,
                  padding: "2px 9px",
                  borderRadius: 4,
                  justifySelf: "start",
                  whiteSpace: "nowrap",
                }}
              >
                {t.sla || "—"}
              </span>
              <div className="hidden sm:flex flex-wrap gap-1.5">
                {integrations.length > 0 ? integrations.map((tag, idx) => (
                  <IntegrationTag key={idx} text={tag} />
                )) : (
                  <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#6b7388" }}>—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
